import { useEffect, useRef, useState } from 'react';

function useEscClose(onClose: () => void) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
}

// Подтверждение действия (обычно — удаление). onConfirm выполняет работу;
// при успехе окно закрывается, при ошибке — остаётся с текстом ошибки.
export function ConfirmModal({
  title,
  message,
  confirmLabel = 'Удалить',
  onConfirm,
  onClose,
}: {
  title: string;
  message: string;
  confirmLabel?: string;
  onConfirm: () => Promise<void>;
  onClose: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEscClose(onClose);

  async function go() {
    setBusy(true);
    setErr(null);
    try {
      await onConfirm();
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Ошибка.');
      setBusy(false);
    }
  }

  return (
    <div className="ovl" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-h">
          <div className="grow">
            <div className="m-cli">{title}</div>
          </div>
          <button className="icon-btn close-x" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="mbody">
          <p style={{ marginBottom: 14 }}>{message}</p>
          {err && <p className="err">{err}</p>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn ghost sm" disabled={busy} onClick={onClose}>
              Отмена
            </button>
            <button className="btn sm" disabled={busy} onClick={go}>
              {busy ? '…' : confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Ввод нового пароля. Валидация — не короче 6 символов.
export function PasswordModal({
  title,
  onSubmit,
  onClose,
}: {
  title: string;
  onSubmit: (password: string) => Promise<void>;
  onClose: () => void;
}) {
  const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const ref = useRef<HTMLInputElement>(null);
  useEscClose(onClose);
  useEffect(() => {
    ref.current?.focus();
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (pw.length < 6) {
      setErr('Пароль не короче 6 символов.');
      ref.current?.focus();
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await onSubmit(pw);
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Не удалось сохранить пароль.');
      setBusy(false);
    }
  }

  return (
    <div className="ovl" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-h">
          <div className="grow">
            <div className="m-cli">{title}</div>
          </div>
          <button className="icon-btn close-x" onClick={onClose}>
            ✕
          </button>
        </div>
        <form className="mbody" onSubmit={submit}>
          {err && <p className="err">{err}</p>}
          <label className="fld">
            <span className="fld-l">Новый пароль</span>
            <input
              ref={ref}
              type="password"
              value={pw}
              disabled={busy}
              placeholder="не короче 6 символов"
              autoComplete="new-password"
              onChange={(e) => setPw(e.target.value)}
            />
          </label>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 6 }}>
            <button type="button" className="btn ghost sm" disabled={busy} onClick={onClose}>
              Отмена
            </button>
            <button type="submit" className="btn sm" disabled={busy}>
              {busy ? 'Сохраняем…' : 'Сохранить'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
